import { useState } from 'react';
import { Color, Size, suggestionRowsFor, t, type SuggestionRow, type SuggestionSource } from '@bms2/shared';
import { Button, Dropdown } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { PAGES_PATH, PICKER_CAP, apiFetch, post } from './api';
import styles from './DocumentPage.module.css';
import { useAddNew } from './AddNewHost';

/**
 * 🔒 A document says which document it came from, after the fact (decision
 * 0469). From an Invoice you name its Sales Order; from that order you name its
 * Quote.
 *
 * 🔨 A Draft and no further, which is the whole rule. An Invoice's approval is
 * derived from this link rather than stored, so attaching past Draft would move
 * it underneath a document already sent. Past Draft this panel draws the link
 * as a plain sentence and offers nothing.
 */
export interface SourceLinkPanelProps {
  /** The document being linked — what its route is, and the id under it. */
  path: string;
  recordId: string;
  /** Which kind of document it is being attached to. */
  sourceKind: 'SalesOrder' | 'Quote';
  /** What the link says now, and where it points; both absent where nothing is linked. */
  sourceLabel?: string | null;
  sourceRoute?: string | null;
  /** The link is only editable on a Draft. */
  editable: boolean;
  apiBaseUrl?: string;
  onChanged: () => void;
}

const FIELD = { SalesOrder: 'salesOrderId', Quote: 'quoteId' } as const;
const ROUTE = { SalesOrder: 'source-sales-order', Quote: 'source-quote' } as const;

export function SourceLinkPanel({ path, recordId, sourceKind, sourceLabel, sourceRoute, editable, apiBaseUrl = '', onChanged }: SourceLinkPanelProps) {
  const addNew = useAddNew(apiBaseUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const search = async (term: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/${sourceKind}?${PICKER_CAP}&q=${encodeURIComponent(term)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: SuggestionSource[] };
    return suggestionRowsFor(sourceKind, body.rows);
  };

  const write = async (id: string | null): Promise<void> => {
    setBusy(true);
    const answer = await post<unknown>(`${path}/${recordId}/${ROUTE[sourceKind]}`, { [FIELD[sourceKind]]: id }, apiBaseUrl);
    setBusy(false);
    setPicking(false);
    // 🔨 The server's own sentence, which already names what to do about it (decision 0458).
    if (!answer.ok) return setError(answer.message);
    setError(null);
    onChanged();
  };

  const heading = sourceKind === 'SalesOrder' ? t('sourceLink.order.title') : t('sourceLink.quote.title');

  return (
    <section className={styles.block} data-source-link={sourceKind}>
      <h2 className={styles.heading}>{heading}</h2>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}

      {sourceLabel ? (
        <p className={styles.muted} data-source-link-current>
          {sourceRoute ? <a className={styles.rowAction} href={sourceRoute}>{sourceLabel}</a> : sourceLabel}
        </p>
      ) : (
        <p className={styles.muted} data-source-link-empty>{t('sourceLink.none')}</p>
      )}

      {!editable ? (
        /* 🔨 Not an error and not a control: a sentence saying why there is nothing to press. */
        <p className={styles.muted} data-source-link-locked>{t('sourceLink.draftOnly')}</p>
      ) : picking ? (
        <div className={styles.blockActions} data-source-link-picker>
          <Dropdown size={Size.Medium}
            label={heading}
            labelHidden
            entityLabel={heading}
            search={search}
            value={null}
            onSelect={(row) => void write(row.id)}
            onAddNew={() => addNew.open(sourceKind === 'Quote' ? { kind: 'record', recordType: 'Quote' } : { kind: 'document', documentType: 'SalesOrder' }, (row) => void write(row.id))}
          />
          <Button size={Size.Small} text={[t('modal.cancel')]} actions={[() => setPicking(false)]} disabled={busy} />
        </div>
      ) : (
        <div className={styles.blockActions}>
          <Button size={Size.Small} color={Color.Navy} text={[sourceLabel ? t('sourceLink.change') : t('sourceLink.attach')]} actions={[() => setPicking(true)]} disabled={busy} />
          {sourceLabel ? <Button size={Size.Small} text={[t('sourceLink.unlink')]} actions={[() => void write(null)]} disabled={busy} /> : null}
        </div>
      )}
      {addNew.host}
    </section>
  );
}
